
import { useState, useEffect } from 'react';
import Form from 'react-bootstrap/Form';
import { Col, Row, Table, Spinner } from 'react-bootstrap';
import ModalBtn from '../../molecules/modalButton/view';
import ButtonPrimary from '../../atoms/ButtonPrimary/view';
import { AiOutlineAudit } from 'react-icons/ai'; // Icon for tax closing or similar
import { dispatchCustomEvent, listener } from '../../../utils/dispatch';
import { getTaxClosingStatus, calculateTaxClosing, approveTaxClosing } from '../../../usecases/accounting-usecase';
import { getThirdById } from '../../../usecases/thirds-usecase';
import { getSubContableAcounts } from '../../../usecases/contable-acounts-usecase';
import MultiSubaccountSearchDropdown from '../../molecules/MultiSubaccountSearchDropdown/view';
import SubaccountSearchDropdown from '../../molecules/SubaccountSearchDropdown/view';
import ClientInfo from '../../atoms/ClientInfo/view';
import { FiCheckCircle, FiAlertTriangle, FiInfo } from 'react-icons/fi';
import Tooltip from '@mui/material/Tooltip';
import './style.css';

function TaxClosureModal({ id, year: initialYear }) {

    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(null); // 'DRAFT', 'APPROVED', or null 
    const [year, setYear] = useState(initialYear || new Date().getFullYear());
    const [month, setMonth] = useState(new Date().getMonth() + 1); // 1-12
    const [selectedAccounts, setSelectedAccounts] = useState([]);
    const [fiscalPartner, setFiscalPartner] = useState(undefined);
    const [payableAccount, setPayableAccount] = useState(undefined);
    const [receivableAccount, setReceivableAccount] = useState(undefined);
    const [previewData, setPreviewData] = useState(null);

    // Initial load logic if needed, e.g. check if month is already closed
    useEffect(() => {
        if (year && month) {
            checkStatus();
        }
        listener("btn-thirds-add", handleThird);
    }, [year, month]);

    useEffect(() => {
        if (fiscalPartner?.id && payableAccount?.id && receivableAccount?.id && selectedAccounts.length > 0) {
            handleCalculate();
        }
    }, [fiscalPartner, payableAccount, receivableAccount, selectedAccounts, year, month]);

    useEffect(() => {
        // Reiniciar el formulario cuando el modal se cierra
        const cleanupClose = listener(`close-${id}-modal`, resetForm);
        const cleanupModalClose = listener(`modal-close-${id}`, resetForm);

        return () => {
            if (cleanupClose) cleanupClose();
            if (cleanupModalClose) cleanupModalClose();
        };
    }, [id]);

    function resetForm() {
        setYear(initialYear || new Date().getFullYear());
        setMonth(new Date().getMonth() + 1);
        setSelectedAccounts([]);
        setFiscalPartner(undefined);
        setPayableAccount(undefined);
        setReceivableAccount(undefined);
        setPreviewData(null);
        setStatus(null);
    }

    function handleThird(event) {
        setFiscalPartner(event.detail.data);
    }

    async function checkStatus() {
        setLoading(true);
        try {
            const result = await getTaxClosingStatus(year, month);
            if (result) {
                setStatus(result.status);
                // Load saved Draft configuration with real names
                if (result.partner_id) {
                    getThirdById(result.partner_id).then(setFiscalPartner);
                }
            } else {
                setStatus(null);
                setPreviewData(null);
                setFiscalPartner(undefined);
                setPayableAccount(undefined);
                setReceivableAccount(undefined);
                setSelectedAccounts([]);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
            dispatchCustomEvent(`loading-btn`, {});
        }
    }

    async function handleCalculate() {
        if (!fiscalPartner || !payableAccount || !receivableAccount || selectedAccounts.length === 0) {
            return;
        }

        setLoading(true);
        try {
            const payload = {
                year: year,
                month: month,
                fiscal_partner_id: fiscalPartner.id,
                payable_account_id: payableAccount.id, // Assuming selected object has id
                receivable_account_id: receivableAccount.id,
                source_account_ids: selectedAccounts.map(acc => acc.id)
            };
            const result = await calculateTaxClosing(payload);
            setPreviewData(result);
        } catch (error) {
            console.error(error);
            dispatchCustomEvent("popup", { message: "Error al calcular cierre.", type: "error" });
            setPreviewData(null);
        } finally {
            setLoading(false);
            dispatchCustomEvent(`loading-btn`, {});
        }
    }

    async function handleApprove() {
        if (!previewData) return;

        setLoading(true);
        try {
            const payload = {
                year: year,
                month: month,
                fiscal_partner_id: fiscalPartner.id,
                payable_account_id: payableAccount.id,
                receivable_account_id: receivableAccount.id,
                source_account_ids: selectedAccounts.map(acc => acc.id)
            };
            await approveTaxClosing(payload);
            setStatus('APPROVED');
            dispatchCustomEvent("popup", { message: "Cierre aprobado exitosamente.", type: "success" });
            dispatchCustomEvent(`close-${id}-modal`, []);
        } catch (error) {
            console.error(error);
            dispatchCustomEvent("popup", { message: "Error al aprobar cierre.", type: "error" });
        } finally {
            setLoading(false);
            dispatchCustomEvent(`loading-btn`, {});
        }
    }

    return (
        <ModalBtn
            id={id}
            icon={<AiOutlineAudit />}
            title={`Cierre de Impuestos`}
            style={{ width: '65vw', maxWidth: '95vw' }}
        >
            <div className="new-tax-closure-container">
                {/* Left Column (30%) */}
                <div className="new-tax-closure-sidebar">
                    <h5 className="new-tax-closure-section-title">Cierre de Impuestos</h5>
                    <p className="new-tax-closure-section-desc">
                        Este proceso permite netear las cuentas de impuestos (IVA, retenciones, etc.) seleccionadas, calcular el saldo neto a pagar o a favor del periodo fiscal, y generar el asiento contable respectivo contra el tercero fiscal correspondiente.
                    </p>
                </div>

                {/* Right Column (70%) */}
                <div className="new-tax-closure-form-right">
                    <div className="new-tax-closure-fields-row" style={{ display: 'flex', gap: '20px', width: '100%', alignItems: 'flex-end' }}>
                        <Form.Group style={{ flex: 1 }}>
                            <label className="tax-closure-label">Año</label>
                            <Form.Control
                                type="number"
                                value={year}
                                onChange={(e) => setYear(parseInt(e.target.value))}
                            />
                        </Form.Group>
                        <Form.Group style={{ flex: 1 }}>
                            <label className="tax-closure-label">Mes</label>
                            <Form.Select
                                value={month}
                                onChange={(e) => setMonth(parseInt(e.target.value))}
                            >
                                {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                                    <option key={m} value={m}>{new Date(0, m - 1).toLocaleString('default', { month: 'long' })}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <div style={{ flex: 1.2 }}>
                            <div className={`status-alert ${status === 'APPROVED' ? 'approved' : 'pending'}`}>
                                <strong>Estado:</strong> {status === 'APPROVED' ? 'Cerrado' : 'Pendiente'}
                            </div>
                        </div>
                    </div>

                    <hr className="new-tax-closure-separator" />

                    <div className="new-tax-closure-fields-row" style={{ display: 'flex', gap: '20px', width: '100%' }}>
                        <Form.Group style={{ flex: 1 }}>
                            <div className="d-flex align-items-center gap-1 mb-1">
                                <label className="tax-closure-label mb-0">Cuentas a Netear (Selección Múltiple)</label>
                                <Tooltip title="Selecciona las subcuentas contables de impuestos (ej. IVA Generado, IVA Descontable, Retenciones) que deseas cruzar." arrow placement="top">
                                    <span>
                                        <FiInfo className="text-muted cursor-pointer" size={14} />
                                    </span>
                                </Tooltip>
                            </div>
                            <MultiSubaccountSearchDropdown
                                selected={selectedAccounts}
                                onSelected={setSelectedAccounts}
                            />
                        </Form.Group>
                    </div>

                    <hr className="new-tax-closure-separator" />

                    <div className="new-tax-closure-fields-row" style={{ display: 'flex', gap: '20px', width: '100%', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                            <div className="d-flex align-items-center gap-1 mb-1">
                                <label className="tax-closure-label mb-0">Tercero Fiscal (DIAN/Secretaría)</label>
                                <Tooltip title="Selecciona la entidad recaudadora (ej. DIAN o Municipio) asociada a los impuestos correspondientes." arrow placement="top">
                                    <span>
                                        <FiInfo className="text-muted cursor-pointer" size={14} />
                                    </span>
                                </Tooltip>
                            </div>
                            <ClientInfo tercero={fiscalPartner} />
                        </div>
                        <div style={{ flex: 1.5, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <Form.Group>
                                <div className="d-flex align-items-center gap-1 mb-1">
                                    <label className="tax-closure-label mb-0">Cuenta Saldo a Pagar (Pasivo)</label>
                                    <Tooltip title="Cuenta contable de pasivos donde se acreditará el saldo a pagar resultante del cruce de impuestos." arrow placement="top">
                                        <span>
                                            <FiInfo className="text-muted cursor-pointer" size={14} />
                                        </span>
                                    </Tooltip>
                                </div>
                                <SubaccountSearchDropdown
                                    onSelected={setPayableAccount}
                                />
                            </Form.Group>

                            <Form.Group>
                                <div className="d-flex align-items-center gap-1 mb-1">
                                    <label className="tax-closure-label mb-0">Cuenta Saldo a Favor (Activo)</label>
                                    <Tooltip title="Cuenta contable de activos donde se debitará el saldo a favor resultante del cruce de impuestos." arrow placement="top">
                                        <span>
                                            <FiInfo className="text-muted cursor-pointer" size={14} />
                                        </span>
                                    </Tooltip>
                                </div>
                                <SubaccountSearchDropdown
                                    onSelected={setReceivableAccount}
                                />
                            </Form.Group>
                        </div>
                    </div>

                    {loading && (
                        <div className="d-flex flex-column align-items-center justify-content-center p-5 gap-3 border rounded bg-light mt-3" style={{ minHeight: '180px' }}>
                            <Spinner animation="border" variant="primary" />
                            <span className="text-muted fw-bold" style={{ fontSize: '0.88rem' }}>Calculando previsualización del asiento...</span>
                        </div>
                    )}

                    {!loading && previewData && (
                        <>
                            <hr className="new-tax-closure-separator" />
                            <div className="preview-table-container">
                                <h5 className="p-2 bg-light m-0 border-bottom text-primary fw-bold" style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.025em' }}>Previsualización del Asiento</h5>
                                <Table className="preview-table mb-0" hover>
                                    <thead>
                                        <tr>
                                            <th>Cuenta</th>
                                            <th>Tercero</th>
                                            <th>Descripción</th>
                                            <th className="text-end">Débito</th>
                                            <th className="text-end">Crédito</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {previewData.lines.map((line, idx) => (
                                            <tr key={idx}>
                                                <td>{line.account_id}</td>
                                                <td>{line.partner_name}</td>
                                                <td>{line.description}</td>
                                                <td className="amount">{line.debit.toLocaleString('es-CO', { minimumFractionDigits: 2 })}</td>
                                                <td className="amount">{line.credit.toLocaleString('es-CO', { minimumFractionDigits: 2 })}</td>
                                            </tr>
                                        ))}
                                        <tr className="fw-bold bg-light">
                                            <td colSpan={3} className="text-end">Total Operación:</td>
                                            <td className="amount text-success text-end">
                                                {previewData.lines.reduce((acc, curr) => acc + curr.debit, 0).toLocaleString('es-CO', { minimumFractionDigits: 2 })}
                                            </td>
                                            <td className="amount text-success text-end">
                                                {previewData.lines.reduce((acc, curr) => acc + curr.credit, 0).toLocaleString('es-CO', { minimumFractionDigits: 2 })}
                                            </td>
                                        </tr>
                                    </tbody>
                                </Table>
                                <div className="p-3 text-end bg-light border-top">
                                    <ButtonPrimary
                                        onClick={handleApprove}
                                        showLoading={true}
                                        disabled={loading || status === 'APPROVED' || !previewData || previewData.lines.reduce((acc, curr) => acc + curr.debit, 0) === 0}
                                        title={status === 'APPROVED' ? "Cierre ya realizado" : "Confirmar y Cerrar Mes"}
                                        style={{ backgroundColor: 'var(--color-success)', color: 'white', border: 'none' }}
                                    />
                                </div>
                            </div>
                        </>
                    )}

                </div>
            </div>
        </ModalBtn>
    );
}

export default TaxClosureModal;
